import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Maximize2, X, Loader2 } from 'lucide-react';

/**
 * Shared caregiver setup-QR block (FECOA-1428/1429/1430/1431).
 *
 * Renders the setup QR for a single caregiver plus their Caregiver ID and
 * (when known) Temporary PIN, with an "Enlarge for Scanning" mode that takes
 * over the whole screen — hiding all other PII while the device faces the
 * caregiver (per the FECOA-1428 privacy note).
 *
 * The QR encodes a setup URL pointing at the caregiver login that pre-fills
 * the Study Code, the Caregiver ID and (when we still hold it at creation
 * time) the Temporary PIN. When no PIN is available, e.g. retrieving the QR
 * later from the detail screen, only the Study Code and ID are encoded and
 * shown. Caregivers sign in with the same Study Code as the participants they
 * care for (FECOA-2156), so the card shows it next to their ID.
 */

/**
 * Build the setup URL the QR encodes. The caregiver login pre-fills `study`,
 * `cg` and `pin` from the query string. Only when the study is unknown does
 * the link fall back to the sponsor in the hash, the form links had before
 * FECOA-2156, which the caregiver login still accepts.
 */
export const buildCaregiverSetupUrl = ({ caregiverId, tempPin, studyCode, sponsorId }) => {
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : '';
  const params = new URLSearchParams();
  if (studyCode) params.set('study', studyCode);
  if (caregiverId) params.set('cg', caregiverId);
  if (tempPin) params.set('pin', tempPin);
  const query = params.toString();
  const hash = !studyCode && sponsorId ? `#${encodeURIComponent(sponsorId)}` : '';
  return `${origin}/caregiver-login${query ? `?${query}` : ''}${hash}`;
};

const useQrDataUrl = (text, width) => {
  const [dataUrl, setDataUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!text) {
      setDataUrl('');
      return undefined;
    }
    QRCode.toDataURL(text, { width, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [text, width]);
  return dataUrl;
};

const CaregiverSetupQR = ({ caregiverId, tempPin, studyCode, sponsorId, relationship, name }) => {
  const [enlarged, setEnlarged] = useState(false);
  const setupUrl = buildCaregiverSetupUrl({ caregiverId, tempPin, studyCode, sponsorId });
  const qrUrl = useQrDataUrl(setupUrl, 320);
  const qrUrlLarge = useQrDataUrl(enlarged ? setupUrl : '', 480);

  return (
    <div className="flex flex-col items-center">
      {name && (
        <div className="text-center mb-3">
          <p className="text-base font-semibold text-gray-900 dark:text-white">{name}</p>
          {relationship && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{relationship}</p>
          )}
        </div>
      )}

      <div className="rounded-xl bg-white p-4 shadow-card ring-1 ring-gray-900/5 dark:bg-white">
        {qrUrl ? (
          <img
            src={qrUrl}
            alt={`Setup QR code for ${caregiverId}`}
            className="w-48 h-48"
          />
        ) : (
          <div className="w-48 h-48 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        )}
      </div>

      {studyCode && (
        <div className="mt-4 text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Study Code</p>
          <p className="text-lg font-mono font-semibold text-primary-600 dark:text-primary-400">
            {studyCode}
          </p>
        </div>
      )}

      <div className={`${studyCode ? 'mt-2' : 'mt-4'} text-center`}>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Caregiver ID</p>
        <p className="text-lg font-mono font-semibold text-primary-600 dark:text-primary-400">
          {caregiverId}
        </p>
      </div>

      {tempPin && (
        <div className="mt-2 text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Temporary PIN</p>
          <p className="text-lg font-mono font-semibold tracking-widest text-primary-600 dark:text-primary-400">
            {tempPin}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setEnlarged(true)}
        className="btn btn-secondary mt-4 w-full"
      >
        <Maximize2 className="h-4 w-4" />
        Enlarge for Scanning
      </button>

      {/* Fullscreen scan mode — hides all other PII while the device faces the
          caregiver (FECOA-1428 privacy note). Only the QR + this caregiver's
          own Study Code/ID/PIN are visible. */}
      {enlarged && (
        <div className="fixed inset-0 z-[60] flex animate-fade-in flex-col items-center justify-center bg-gray-900 px-4">
          <button
            type="button"
            onClick={() => setEnlarged(false)}
            aria-label="Close scan view"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="rounded-xl bg-white p-5 shadow-overlay dark:bg-white">
            {qrUrlLarge ? (
              <img
                src={qrUrlLarge}
                alt={`Setup QR code for ${caregiverId}`}
                className="w-64 h-64 sm:w-72 sm:h-72"
              />
            ) : (
              <div className="w-64 h-64 sm:w-72 sm:h-72 flex items-center justify-center">
                <Loader2 className="h-7 w-7 animate-spin text-primary-600" />
              </div>
            )}
          </div>
          {studyCode && (
            <div className="mt-5 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Study Code</p>
              <p className="text-xl font-mono font-semibold text-primary-400">{studyCode}</p>
            </div>
          )}
          <div className={`${studyCode ? 'mt-3' : 'mt-5'} text-center`}>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Caregiver ID</p>
            <p className="text-xl font-mono font-semibold text-primary-400">{caregiverId}</p>
          </div>
          {tempPin && (
            <div className="mt-3 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Temporary PIN</p>
              <p className="text-xl font-mono font-semibold tracking-widest text-primary-400">
                {tempPin}
              </p>
            </div>
          )}
          <p className="mt-4 text-xs text-gray-400 text-center max-w-xs">
            Scan the QR code or enter the credentials above to complete setup.
          </p>
        </div>
      )}
    </div>
  );
};

export default CaregiverSetupQR;
